import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { CSPProvider } from '@base-ui/react/csp-provider';
import { Toaster } from 'sonner';
import { router } from './router';
import { ConfirmProvider } from './components/ui';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A family file store changes when someone in the house changes it, not
      // continuously, so refetching on every window focus is pure noise.
      refetchOnWindowFocus: false,
      staleTime: 10_000,
      retry: (failureCount, error) => {
        // Never retry an authorisation failure: it will fail identically, and
        // the 401 handler has already sent the user to sign in.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

createRoot(root).render(
  <StrictMode>
    {/*
      The page is served `style-src 'self'` with no 'unsafe-inline', so the
      <style> element Base UI would otherwise inject for its scroll-hiding is
      turned off here; styles.css carries the same rules instead.
    */}
    <CSPProvider disableStyleElements>
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <RouterProvider router={router} />
          <Toaster position="bottom-center" closeButton richColors />
        </ConfirmProvider>
      </QueryClientProvider>
    </CSPProvider>
  </StrictMode>,
);
